/**
 * Favorites (Wishlist) Manager
 * Stores product handles in localStorage and dispatches custom events.
 *
 * Usage:
 *   Favorites.add('product-handle');
 *   Favorites.remove('product-handle');
 *   Favorites.toggle('product-handle');
 *   Favorites.has('product-handle');   // → boolean
 *   Favorites.getAll();                // → ['handle-a', 'handle-b']
 *   Favorites.count();                 // → number
 *   Favorites.clear();
 *
 * Events (dispatched on document):
 *   'favorites:changed'  → detail: { handles: [...], count: number }
 *   'favorites:added'    → detail: { handle: 'product-handle' }
 *   'favorites:removed'  → detail: { handle: 'product-handle' }
 */

const Favorites = (() => {
  const STORAGE_KEY = 'shopify-favorites';

  function _read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function _write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      console.warn('[Favorites] localStorage write failed', e);
    }
  }

  function _dispatch(eventName, detail) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  function _emitChanged() {
    const handles = _read();
    _dispatch('favorites:changed', { handles, count: handles.length });
  }

  return {
    getAll() {
      return _read();
    },

    count() {
      return _read().length;
    },

    has(handle) {
      return _read().includes(handle);
    },

    add(handle) {
      const handles = _read();
      if (!handles.includes(handle)) {
        handles.push(handle);
        _write(handles);
        _dispatch('favorites:added', { handle });
        _emitChanged();
      }
    },

    remove(handle) {
      let handles = _read();
      const index = handles.indexOf(handle);
      if (index > -1) {
        handles.splice(index, 1);
        _write(handles);
        _dispatch('favorites:removed', { handle });
        _emitChanged();
      }
    },

    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      } else {
        this.add(handle);
        return true;
      }
    },

    clear() {
      _write([]);
      _emitChanged();
    }
  };
})();